import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackView, trackDuration } from './track';

/**
 * Records a page view per route, and how long each was on screen.
 *
 * <p>The view id lives in a ref — in this tab's memory, nowhere else. Nothing is written to the
 * device, which is what keeps this outside PECR's consent requirement.
 *
 * <p>Two things it deliberately gets right:
 * <ul>
 *   <li><b>Time is counted while visible, not while open.</b> A tab left in the background for an
 *       hour would otherwise report an hour of reading. The visibility handler banks the elapsed
 *       time on hide and restarts the clock on show.</li>
 *   <li><b>It ends on `pagehide`, not `unload`.</b> `unload` doesn't fire reliably on mobile
 *       Safari or when a page is restored from the back/forward cache, which is exactly when
 *       durations would go missing.</li>
 * </ul>
 */
export function usePageTracking(): void {
  const { pathname } = useLocation();
  const viewIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<number>(0);
  const accumulatedRef = useRef<number>(0);
  const endedRef = useRef(false);

  useEffect(() => {
    const viewId = crypto.randomUUID();
    viewIdRef.current = viewId;
    startedAtRef.current = performance.now();
    accumulatedRef.current = 0;
    endedRef.current = false;

    // Only an external referrer is interesting; in-app navigation is already the previous view.
    const referrer = document.referrer && !document.referrer.startsWith(window.location.origin)
      ? document.referrer
      : undefined;

    trackView(viewId, pathname, referrer);

    const visibleMs = () => {
      const live = startedAtRef.current > 0 ? performance.now() - startedAtRef.current : 0;
      return Math.round(accumulatedRef.current + live);
    };

    const end = () => {
      if (endedRef.current) return; // pagehide can follow a visibilitychange
      endedRef.current = true;
      trackDuration(viewId, visibleMs());
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        accumulatedRef.current += performance.now() - startedAtRef.current;
        startedAtRef.current = 0;
        end();
      } else {
        // Returning to a page we already closed off starts a fresh clock rather than
        // double-counting the time it spent hidden.
        startedAtRef.current = performance.now();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', end);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', end);
      end(); // navigating away within the app closes this view too
    };
  }, [pathname]);
}
